import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";

type RequestMeta = {
  service?: "default" | "payment";
  isAuthenticated?: boolean;
};

type RequestInput<P = unknown> = RequestMeta & {
  url: string;
  method: AxiosRequestConfig["method"];
  payload?: P;
};

type AdditionalConfig = Omit<AxiosRequestConfig, "url" | "data" | "baseURL" | "method">;

class HttpService {
  private axios: AxiosInstance;

  private static baseConfig = {
    baseURL: import.meta.env.VITE_DATABASE_ENDPOINT,
    headers: {
      "Content-Type": "application/json",
    },
  } satisfies AxiosRequestConfig;

  constructor() {
    this.axios = axios.create(HttpService.baseConfig);
    this.attachInterceptors();
  }

  request = async <T = unknown>(
    { url, method, payload, service = "default", isAuthenticated = true }: RequestInput,
    additionalConfig?: AdditionalConfig,
  ): Promise<T> => {
    const config: AxiosRequestConfig = {
      ...HttpService.baseConfig,
      ...additionalConfig,
    };
    config.url = url;
    config.method = method;
    config.data = payload;

    if (service === "payment") config.baseURL = import.meta.env.VITE_PAYMENT_DATABASE_ENDPOINT;
    if (isAuthenticated) {
      const token = Cookies.get("token") ?? undefined;
      config.headers = { ...config.headers, token, token_type: "bearer" };
    }

    const response = await this.axios.request(config);
    return response.data;
  };

  post = async <T = unknown, P = unknown>(
    url: string,
    payload?: P,
    options?: RequestMeta,
    additionalConfig?: AdditionalConfig,
  ) => this.request<T>({ ...options, url, payload, method: "POST" }, additionalConfig);

  get = async <T = unknown>(url: string, options?: RequestMeta, additionalConfig?: AdditionalConfig) =>
    this.request<T>({ ...options, url, method: "GET" }, additionalConfig);

  private attachInterceptors(): void {
    this.axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.status || error?.response.status;
        if (status == 401) {
          toast.error("");
          window.location.assign("/auth");
        }
        if (status == 403) {
          toast.error("کاربر دسترسی ندارد");
          // remove this: redirect("/auth");
        }
        return Promise.reject(error);
      },
    );
  }
}

export const httpService = new HttpService();
